import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Check, ShieldCheck, Clock } from 'lucide-react';
import { api } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../lib/i18n';

export default function Collaborators() {
  const { t } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [collabs, setCollabs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [skillFilter, setSkillFilter] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ skills: [], bio: '', availability_hours_week: 4, verified_credentials: '', contact_email: '' });
  const [submitting, setSubmitting] = useState(false);

  const load = async (skill = '') => {
    setLoading(true);
    try { const { data } = await api.get('/collaborators' + (skill ? `?skill=${skill}` : '')); setCollabs(data); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(skillFilter); }, [skillFilter]);

  const toggleSkill = (k) => setForm((f) => ({ ...f, skills: f.skills.includes(k) ? f.skills.filter(x => x !== k) : [...f.skills, k] }));

  const submit = async (e) => {
    e.preventDefault();
    if (!user) { navigate('/login'); return; }
    if (form.skills.length === 0 || form.bio.length < 20) return;
    setSubmitting(true);
    try {
      const payload = { ...form };
      if (!payload.verified_credentials) delete payload.verified_credentials;
      if (!payload.contact_email) delete payload.contact_email;
      await api.post('/collaborators', payload);
      toast.success(t.collaborators.updated);
      setShowForm(false);
      load(skillFilter);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t.common.error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
      <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium mb-4">Tikkun</p>
      <h1 className="font-display text-5xl md:text-6xl font-medium tracking-tight leading-tight">{t.collaborators.title}</h1>
      <p className="mt-4 text-lg text-muted-foreground max-w-2xl">{t.collaborators.subtitle}</p>

      <div className="mt-10 flex flex-wrap items-center gap-3">
        <select value={skillFilter} onChange={(e) => setSkillFilter(e.target.value)} data-testid="collab-skill-filter"
          className="px-4 py-2.5 rounded-full bg-secondary border border-transparent focus:border-primary focus:bg-background outline-none text-sm">
          <option value="">{t.collaborators.filter}</option>
          {t.collaborators.skills.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
        </select>
        <button onClick={() => { if (!user) navigate('/login'); else setShowForm(!showForm); }}
          data-testid="collab-offer-btn" className="ml-auto px-5 py-2.5 rounded-full bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 transition-opacity">
          {t.collaborators.offer_cta}
        </button>
      </div>

      {showForm && user && (
        <form onSubmit={submit} className="mt-8 p-8 rounded-3xl border border-border bg-card space-y-5" data-testid="collab-offer-form">
          <h3 className="font-display text-xl font-medium">{t.collaborators.offer_title}</h3>
          <div>
            <label className="text-xs uppercase tracking-widest text-muted-foreground font-medium">{t.collaborators.skills_label}</label>
            <div className="mt-3 flex flex-wrap gap-2">
              {t.collaborators.skills.map((s) => {
                const active = form.skills.includes(s.key);
                return (
                  <button type="button" key={s.key} onClick={() => toggleSkill(s.key)} data-testid={`collab-skill-${s.key}`}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${active ? 'bg-primary text-primary-foreground' : 'bg-secondary text-foreground hover:bg-secondary/70'}`}>
                    {s.label}
                    {active && <Check size={14} className="inline ml-1.5" />}
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-muted-foreground font-medium">{t.collaborators.bio_label}</label>
            <textarea rows={4} value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} placeholder={t.collaborators.bio_ph}
              data-testid="collab-bio-input" required minLength={20}
              className="mt-2 w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:ring-1 focus:ring-primary outline-none resize-none" />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs uppercase tracking-widest text-muted-foreground font-medium">{t.collaborators.hours_label}</label>
              <input type="number" min="1" max="40" value={form.availability_hours_week} onChange={(e) => setForm({ ...form, availability_hours_week: parseInt(e.target.value) || 1 })}
                data-testid="collab-hours-input" className="mt-2 w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary outline-none" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-muted-foreground font-medium">{t.collaborators.email_label}</label>
              <input type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
                data-testid="collab-email-input" className="mt-2 w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary outline-none" />
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-muted-foreground font-medium">{t.collaborators.credentials_label}</label>
            <input type="text" value={form.verified_credentials} onChange={(e) => setForm({ ...form, verified_credentials: e.target.value })}
              data-testid="collab-creds-input" className="mt-2 w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary outline-none" />
          </div>
          <button type="submit" disabled={submitting || form.skills.length === 0 || form.bio.length < 20} data-testid="collab-submit-btn"
            className="px-6 py-2.5 rounded-full bg-primary text-primary-foreground font-medium hover:opacity-90 disabled:opacity-40 transition-opacity">
            {submitting ? t.common.loading : t.collaborators.submit}
          </button>
        </form>
      )}

      <div className="mt-14" data-testid="collab-list">
        {loading ? <p className="text-muted-foreground">{t.common.loading}</p> : collabs.length === 0 ? (
          <p className="text-muted-foreground text-center py-16">—</p>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {collabs.map((c) => (
              <div key={c.id} data-testid={`collab-card-${c.id}`} className="p-7 rounded-3xl border border-border bg-card">
                <div className="flex items-center justify-between mb-4">
                  <div className="font-medium text-lg">{c.author_name}</div>
                  <span className={`text-xs px-2.5 py-1 rounded-full inline-flex items-center gap-1 ${c.verified ? 'text-emerald-600 bg-emerald-500/10' : 'text-muted-foreground bg-secondary'}`}>
                    {c.verified ? <><ShieldCheck size={12} /> {t.collaborators.verified}</> : <><Clock size={12} /> {t.collaborators.unverified}</>}
                  </span>
                </div>
                <div className="flex flex-wrap gap-1.5 mb-4">
                  {c.skills.map((s) => {
                    const label = t.collaborators.skills.find(x => x.key === s)?.label || s;
                    return <span key={s} className="text-xs px-2.5 py-1 rounded-full bg-secondary text-foreground">{label}</span>;
                  })}
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed line-clamp-4">{c.bio}</p>
                <div className="mt-4 pt-4 border-t border-border text-xs text-muted-foreground">
                  {c.availability_hours_week} {t.collaborators.hours_week}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
